import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-markdown-input.js";
import "./pd-rating-stars.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
//#region src/pd-rating/PdRatingInput.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
/**
* valueName of the embedded markdown input. Unique on purpose: its
* `pd-form-element-unregister` is dispatched on `document` and cannot be
* swallowed at the component boundary — an unknown name makes it a no-op
* in any surrounding `pd-form-container`.
*/
var TEXT_NAME = "__pdri_text";
var PdRatingInput = class PdRatingInput extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.requireText = true;
		this.withRating = true;
		this.hideActions = false;
		this._rating = null;
		this._errorMsg = null;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      --pd-rating-star-size: 1.5rem;
    }

    .field-label {
      display: block;
      margin-bottom: 0.25rem;
      font-size: 0.85em;
      color: var(--pd-default-font-muted-col, #757575);
    }

    .stars-field {
      margin-bottom: 0.75rem;
    }

    .error {
      margin: 0.35rem 0 0;
      font-size: 0.85em;
      color: var(--pd-default-error-col, #c62828);
    }

    .actions {
      display: flex;
      gap: 0.5rem;
      justify-content: flex-end;
      margin-top: 0.75rem;
    }
  `;
	}
	willUpdate(changedProps) {
		if (changedProps.has("value")) this._rating = this.value?.rating ?? null;
	}
	render() {
		return html`
      <div
        @pd-form-element-register="${this._swallow}"
        @pd-form-element-blur="${this._swallow}"
        @pd-form-element-focus="${this._swallow}"
        @pd-form-element-change="${this._swallow}"
      >
        ${this.withRating ? html`
                <div class="stars-field">
                  <span class="field-label" id="starsLabelId">
                    ${msg("Bewertung (optional)", { id: "pd.rating.input.starsLabel" })}
                  </span>
                  <pd-rating-stars
                    interactive
                    allowClear
                    .value="${this._rating ?? 0}"
                    @pd-rating-stars-change="${this._onStarsChange}"
                  ></pd-rating-stars>
                </div>
              ` : nothing}

        <pd-markdown-input
          valueName="${TEXT_NAME}"
          .label="${msg("Erfahrungsbericht", { id: "pd.rating.input.text" })}"
          .initValue="${this.value?.text ?? ""}"
        ></pd-markdown-input>

        ${this._errorMsg ? html`<p class="error" role="alert">${this._errorMsg}</p>` : nothing}
        ${this.hideActions ? nothing : html`
                <div class="actions">
                  <pd-button
                    .text="${this.cancelLabel ?? msg("Abbrechen", { id: "pd.rating.input.cancel" })}"
                    @click="${this._handleCancel}"
                  ></pd-button>
                  <pd-button
                    primary
                    .text="${this.submitLabel ?? msg("Bewertung senden", { id: "pd.rating.input.submit" })}"
                    @click="${this._handleSubmit}"
                  ></pd-button>
                </div>
              `}
      </div>
    `;
	}
	/** Programmatic submit for hosts with own action row (hideActions):
	*  validates and emits pd-rating-submit exactly like the built-in button. */
	submit() {
		this._handleSubmit();
	}
	/** Programmatic cancel counterpart: resets and emits pd-rating-cancel. */
	cancel() {
		this._handleCancel();
	}
	_swallow(e) {
		e.stopPropagation();
	}
	_onStarsChange(e) {
		this._rating = e.detail?.value ?? null;
		e.stopPropagation();
	}
	_handleSubmit() {
		const text = (this._textInput?.value ?? "").trim();
		if (this.requireText && !text) {
			this._errorMsg = msg("Bitte einen Text eingeben.", { id: "pd.rating.input.errorText" });
			return;
		}
		this._errorMsg = null;
		this.dispatchEvent(new CustomEvent("pd-rating-submit", {
			detail: {
				rating: this._rating,
				text
			},
			bubbles: true,
			composed: true
		}));
		this._reset();
	}
	_handleCancel() {
		this._errorMsg = null;
		this._reset();
		this.dispatchEvent(new CustomEvent("pd-rating-cancel", {
			bubbles: true,
			composed: true
		}));
	}
	/** Fresh form for the next open — the emitted event carries the data. */
	_reset() {
		this._rating = this.value?.rating ?? null;
		this._textInput?.reset();
	}
};
__decorate([property({ attribute: false })], PdRatingInput.prototype, "value", void 0);
__decorate([property({ type: Boolean })], PdRatingInput.prototype, "requireText", void 0);
__decorate([property({ type: Boolean })], PdRatingInput.prototype, "withRating", void 0);
__decorate([property()], PdRatingInput.prototype, "submitLabel", void 0);
__decorate([property()], PdRatingInput.prototype, "cancelLabel", void 0);
__decorate([property({ type: Boolean })], PdRatingInput.prototype, "hideActions", void 0);
__decorate([state()], PdRatingInput.prototype, "_rating", void 0);
__decorate([state()], PdRatingInput.prototype, "_errorMsg", void 0);
__decorate([query("pd-markdown-input")], PdRatingInput.prototype, "_textInput", void 0);
PdRatingInput = __decorate([localized()], PdRatingInput);
//#endregion
export { PdRatingInput };
